import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

interface JsonToExcelProps {}

const JsonToExcel: React.FC<JsonToExcelProps> = () => {
  // State for JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for successful conversion
  const [isConverted, setIsConverted] = useState<boolean>(false);
  // State for file name
  const [fileName, setFileName] = useState<string>('converted_data');
  // State for file format
  const [fileFormat, setFileFormat] = useState<'xlsx' | 'csv'>('xlsx');
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for sheet name
  const [sheetName, setSheetName] = useState<string>('Sheet1');
  // State for include headers option
  const [includeHeaders, setIncludeHeaders] = useState<boolean>(true);
  // State for notification
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'info' | null, message: string}>({
    type: null,
    message: ''
  });
  
  // Reference to the file input
  const fileInputRef = useRef<HTMLInputElement>(null);
  

  
  // Function to load sample data
  const loadSampleData = () => {
    const formattedSample = JSON.stringify(sampleData, null, 2);
    setJsonInput(formattedSample);
    setError('');
    showNotification('info', 'Sample data loaded!');
  };
  
  // Function to show notification
  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({
      type,
      message
    });
    
    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setNotification({
        type: null,
        message: ''
      });
    }, 3000);
  };
  
  // Function to handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsLoading(true);
    setError('');
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        setJsonInput(text);
        setIsLoading(false);
        showNotification('success', `File "${file.name}" uploaded successfully!`);
      } catch (err) {
        setIsLoading(false);
        setError(`Failed to read the file: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    
    reader.onerror = () => {
      setIsLoading(false);
      setError('Failed to read the file. Please try again.');
    };
    
    reader.readAsText(file);
  };
  
  // Function to validate JSON input
  const validateJson = () => {
    setError('');
    setIsConverted(false);
    
    try {
      // Handle empty input
      if (!jsonInput.trim()) {
        setError('Please enter JSON data or upload a file.');
        return false;
      }
      
      // Parse the JSON to validate it
      const parsed = JSON.parse(jsonInput);
      
      // Check if it's an array or object
      if (Array.isArray(parsed) || (typeof parsed === 'object' && parsed !== null)) {
        setIsConverted(true);
        return true;
      } else {
        setError('Invalid JSON: Data must be an object or an array of objects.');
        return false;
      }
    } catch (err) {
      setError(`Failed to parse JSON: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return false;
    }
  };
  
  // Function to convert JSON to Excel
  const convertToExcel = () => {
    if (!validateJson()) return;
    
    try {
      // Parse the JSON
      const jsonData = JSON.parse(jsonInput);
      
      // Handle single objects by wrapping them in an array
      const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
      
      // Process data for Excel - handle nested objects and arrays
      const processedData = dataArray.map(row => {
        const processed: any = {};
        
        for (const key in row) {
          if (Object.prototype.hasOwnProperty.call(row, key)) {
            const value = row[key];
            
            if (typeof value === 'object' && value !== null) {
              // Convert arrays and objects to strings
              processed[key] = JSON.stringify(value);
            } else {
              processed[key] = value;
            }
          }
        }
        
        return processed;
      });
      
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Create a worksheet
      const ws = XLSX.utils.json_to_sheet(processedData, { 
        header: includeHeaders ? Object.keys(processedData[0]) : undefined 
      });
      
      // Auto-fit columns
      const colWidths: number[] = [];
      for (const row of processedData) {
        Object.keys(row).forEach((key, colIndex) => {
          const value = String(row[key] || '');
          colWidths[colIndex] = Math.max(colWidths[colIndex] || 0, value.length, key.length);
        });
      }
      
      ws['!cols'] = colWidths.map(width => ({ width: Math.min(Math.max(width + 2, 10), 50) }));
      
      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      
      // Generate the Excel file
      let excelData;
      let contentType = '';
      let extension = '';
      
      if (fileFormat === 'csv') {
        excelData = XLSX.write(wb, { bookType: 'csv', type: 'binary' });
        contentType = 'text/csv;charset=utf-8;';
        extension = 'csv';
      } else {
        excelData = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
        contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8;';
        extension = 'xlsx';
      }
      
      // Convert binary data to Blob
      const buf = new ArrayBuffer(excelData.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < excelData.length; i++) {
        view[i] = excelData.charCodeAt(i) & 0xFF;
      }
      
      // Save the file
      const blob = new Blob([buf], { type: contentType });
      saveAs(blob, `${fileName}.${extension}`);
      
      showNotification('success', `Excel file downloaded as "${fileName}.${extension}"`);
    } catch (err) {
      setError(`Failed to convert to Excel: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };
  
  // Function to clear all inputs
  const clearAll = () => {
    setJsonInput('');
    setError('');
    setIsConverted(false);
    setFileName('converted_data');
    setFileFormat('xlsx');
    setSheetName('Sheet1');
    setIncludeHeaders(true);
    showNotification('info', 'All fields cleared');
  };
  
  // Function to handle drag and drop
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    
    // Check if it's a JSON file
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError('Please upload a JSON file.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        setJsonInput(text);
        setIsLoading(false);
        showNotification('success', `File "${file.name}" uploaded successfully!`);
      } catch (err) {
        setIsLoading(false);
        setError(`Failed to read the file: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    
    reader.onerror = () => {
      setIsLoading(false);
      setError('Failed to read the file. Please try again.');
    };
    
    reader.readAsText(file);
  };
  
  // Prevent default behavior for drag events
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  
  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Excel Converter</h1>
      
      {/* Notification */}
      {notification.type && (
        <div 
          className={`mb-6 p-3 rounded-md ${
            notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
            notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
            'bg-blue-50 border border-blue-200 text-blue-700'
          }`}
        >
          {notification.message}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">JSON Input</h2>
            <div className="flex space-x-2">
              <button
                className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                onClick={loadSampleData}
              >
                Load Sample
              </button>
              <button
                className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                onClick={() => setJsonInput('')}
              >
                Clear
              </button>
            </div>
          </div>
          
          {/* File Upload Area */}
          <div className="mb-4">
            <div
              className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-400 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleFileUpload}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 mx-auto text-gray-400 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-sm text-gray-500">
                Drag & drop a JSON file here, or <span className="text-blue-500">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">Supports .json files</p>
            </div>
          </div>
          
          {/* JSON Input */}
          <div className="mb-4">
            <textarea
              className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Paste your JSON data here...\n\nExample:\n[\n  {\n    "id": 1,\n    "name": "John Doe",\n    "email": "john@example.com"\n  }\n]`}
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              spellCheck="false"
            />
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={validateJson}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Validate JSON'
              )}
            </button>
            
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              onClick={clearAll}
            >
              Clear All
            </button>
          </div>
        </div>
        
        {/* Options Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Excel Options</h2>
            <button
              className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {/* Basic Options */}
          <div className="space-y-4 mb-4">
            <div>
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="e.g., my_data_export"
              />
            </div>
            
            <div>
              <label htmlFor="fileFormat" className="block text-sm font-medium text-gray-700 mb-1">
                File Format
              </label>
              <select
              disabled
                id="fileFormat"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileFormat}
                onChange={(e) => setFileFormat(e.target.value as 'xlsx' | 'csv')}
              >
                <option value="xlsx">Excel (.xlsx)</option>
                {/* <option value="csv">CSV (.csv)</option> */}
              </select>
            </div>
          </div>
          
          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-4 p-4 mb-4 bg-gray-50 rounded-md border border-gray-200">
              <div>
                <label htmlFor="sheetName" className="block text-sm font-medium text-gray-700 mb-1">
                  Sheet Name
                </label>
                <input
                  type="text"
                  id="sheetName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  placeholder="e.g., Sheet1"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeHeaders"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={includeHeaders}
                  onChange={(e) => setIncludeHeaders(e.target.checked)}
                />
                <label htmlFor="includeHeaders" className="ml-2 block text-sm text-gray-700">
                  Include Column Headers
                </label>
              </div>
            </div>
          )}
          
          {/* Download Button */}
          <button
            className={`w-full px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isConverted
                ? 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={convertToExcel}
            disabled={!isConverted}
          >
            <span className="flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download Excel
            </span>
          </button>
          
          {/* Instructions */}
          <div className="mt-6 bg-blue-50 p-4 rounded-md text-sm text-blue-700 border border-blue-100">
            <h3 className="font-medium mb-2">How to use:</h3>
            <ol className="list-decimal list-inside space-y-1">
              <li>Paste your JSON data or upload a JSON file</li>
              <li>Click "Validate JSON" to check your data</li>
              <li>Configure your Excel options</li>
              <li>Click "Download Excel" to convert and download your file</li>
            </ol>
          </div>
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold mb-4">About JSON to Excel Conversion</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How It Works</h3>
            <p className="text-gray-600 text-sm">
              This tool converts JSON data into Excel spreadsheets. It works with both arrays of objects and single objects.
              The converter automatically extracts all properties as columns and maps the values accordingly.
              Complex data types like nested objects and arrays are properly handled and converted to string representation.
            </p>
            
            <h3 className="font-medium text-gray-800 mt-4 mb-2">Supported Features</h3>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>Convert JSON arrays to Excel rows</li>
              <li>Export as XLSX or CSV</li>
              <li>Customize sheet name and file name</li>
              <li>Auto-fit column widths for better readability</li>
              <li>Handle nested objects and arrays</li>
              <li>Drag & drop file upload</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>Ensure your JSON is valid before conversion</li>
              <li>Use arrays of objects for multiple rows</li>
              <li>For large datasets (over 100,000 rows), consider splitting into multiple files</li>
              <li>For date fields, ensure they are in a consistent format</li>
              <li>Customize the file name to something descriptive for better organization</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToExcel;